import type {
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  PaymentsNotReady,
  ProviderConnect,
  StoreConnection,
  StoreNotFound,
  StoreProviderStatus,
  StoreRoleForbidden,
} from "@hobbyshopify/backend-client";
import { type AsyncResult, describeErrors } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { ProvidersRepository } from "../repositories/providers-repository";
import type { StoreRepository } from "../repositories/store-repository";
import type { BackendApi } from "./api";

/** Fetches the shop's connections into the repository. Single flight per shop. */
export async function loadProviders(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  providersRepository: ProvidersRepository;
  storeId: string;
}): Promise<void> {
  const repo = o.providersRepository;
  if (repo.storeId === o.storeId && repo.status.kind === "loading") return;
  repo.setLoading(o.storeId);
  const r = await o.authHandler.callApi((ctx) => o.client.stores.providers(ctx, o.storeId));
  if (repo.storeId !== o.storeId) return;
  if (!r.ok) {
    repo.setError(o.storeId, describeErrors(r.errors));
    return;
  }
  repo.setProviders(o.storeId, r.value.providers, r.value.stripeMode);
}

/** One provider's live status, straight from the provider. */
export function loadProviderStatus(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeId: string;
  providerKey: string;
}): AsyncResult<StoreProviderStatus, ApiNotAuthorized | StoreNotFound | InvalidFormField> {
  return o.authHandler.callApi((ctx) => o.client.stores.provider(ctx, o.storeId, o.providerKey));
}

/** What a connection change touches elsewhere: the list that showed it, and the store row that carries a copy. */
export interface ProviderSideEffects {
  providersRepository: ProvidersRepository;
  storeRepository: StoreRepository;
}

function markChanged(o: ProviderSideEffects): void {
  o.providersRepository.markStale();
  o.storeRepository.markStale();
}

/** Asks the provider for the account's current status and records it. */
export async function refreshProvider(
  o: ProviderSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    storeId: string;
    providerKey: string;
  },
): AsyncResult<StoreConnection, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.stores.refreshProvider(ctx, o.storeId, o.providerKey));
  if (r.ok) markChanged(o);
  return r;
}

export async function validateProvider(
  o: ProviderSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    storeId: string;
    providerKey: string;
  },
): AsyncResult<StoreConnection, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.stores.validateProvider(ctx, o.storeId, o.providerKey));
  if (r.ok) markChanged(o);
  return r;
}

export async function disconnectProvider(
  o: ProviderSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    storeId: string;
    providerKey: string;
  },
): AsyncResult<StoreConnection | null, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.stores.disconnectProvider(ctx, o.storeId, o.providerKey));
  if (r.ok) markChanged(o);
  return r;
}

/** Starts (or resumes) Stripe's hosted onboarding; the answer is where to send the browser. */
export async function connectStripe(
  o: ProviderSideEffects & { client: BackendApi; authHandler: AuthHandler<ApiContext>; storeId: string },
): AsyncResult<ProviderConnect, ApiNotAuthorized | StoreNotFound | PaymentsNotReady | StoreRoleForbidden> {
  const r = await o.authHandler.callApi((ctx) => o.client.stripe.connect(ctx, o.storeId));
  if (r.ok) markChanged(o);
  return r;
}

/** Test mode only: reuse an existing sandbox account instead of onboarding a new one. */
export async function attachStripeAccount(
  o: ProviderSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    storeId: string;
    accountId: string;
  },
): AsyncResult<
  StoreConnection,
  ApiNotAuthorized | StoreNotFound | InvalidFormField | PaymentsNotReady | StoreRoleForbidden
> {
  const r = await o.authHandler.callApi((ctx) => o.client.stripe.attach(ctx, o.storeId, o.accountId));
  if (r.ok) markChanged(o);
  return r;
}

/**
 * The merchant's Stripe dashboard link. Fetched per click and discarded: an
 * older Express account is answered with a single-use, short-lived link, and
 * caching one would hand out a dead URL.
 */
export function stripeDashboardLink(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeId: string;
}): AsyncResult<ProviderConnect, ApiNotAuthorized | StoreNotFound | PaymentsNotReady | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.stripe.dashboard(ctx, o.storeId));
}
